/**
 * RecentDocumentsWidget — P0.18.2 Operational Document Hub (Aug 2026).
 *
 * Dashboard-tegel die de laatste 25 documenten uit ALLE bronnen toont.
 * Klikken opent het document meteen (via de gekoppelde shipment of
 * consolidatie) of downloadt direct via download-icoon.
 *
 * Ontworpen om compact te zijn — past onder de reeds bestaande "System
 * Health" en "Communication" widgets op het admin Dashboard.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FileText, Ship, Plane, FileSpreadsheet, Receipt, ClipboardList,
  Package, BookOpen, Download, ExternalLink, Loader2, RefreshCw,
} from "lucide-react";

import { adminApi, formatApiError } from "../../context/AuthContext";
import { useGlobalLoading } from "../../context/GlobalLoadingProvider";

const TYPE_ICONS = {
  warehouse_receipt: <Package className="w-3 h-3" />,
  shipping_label: <Package className="w-3 h-3" />,
  packing_list: <ClipboardList className="w-3 h-3" />,
  booking_form: <BookOpen className="w-3 h-3" />,
  quote: <FileText className="w-3 h-3" />,
  invoice: <Receipt className="w-3 h-3" />,
  manifest_sea: <Ship className="w-3 h-3" />,
  manifest_air: <Plane className="w-3 h-3" />,
  manifest_xlsx: <FileSpreadsheet className="w-3 h-3" />,
  manifest_csv: <FileText className="w-3 h-3" />,
  manifest_json: <FileText className="w-3 h-3" />,
  manifest_xml: <FileText className="w-3 h-3" />,
};

function fmtTimeAgo(iso) {
  if (!iso) return "-";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return "zojuist";
    if (min < 60) return `${min}m geleden`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}u geleden`;
    const d = Math.floor(h / 24);
    return `${d}d geleden`;
  } catch { return iso; }
}

export default function RecentDocumentsWidget() {
  const { runTask } = useGlobalLoading();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/document-hub/recent?limit=25");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Kon recente documenten niet laden");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDownload = async (doc) => {
    try {
      await runTask({
        id: `recent-doc-${doc.id}`,
        message: `${doc.type_label} wordt gedownload…`,
        successMessage: `${doc.type_label} gedownload`,
        run: async () => {
          const resp = await adminApi.get(doc.pdf_url.replace(/^\/api/, ""), {
            responseType: "blob",
          });
          const url = window.URL.createObjectURL(new Blob([resp.data]));
          const a = document.createElement("a");
          a.href = url;
          const disp = resp.headers["content-disposition"] || "";
          const m = disp.match(/filename="?([^";]+)"?/);
          a.download = m ? m[1] : `${doc.type}-${doc.number}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        },
      });
    } catch (e) {
      if (e?.message !== "DUPLICATE_TASK") {
        toast.error(formatApiError(e) || "Download mislukt");
      }
    }
  };

  const openSource = (doc) => {
    if (doc.w_number) {
      window.open(`/admin?section=warehouse&w=${doc.w_number}`, "_blank");
    } else if (doc.consolidation_id) {
      window.open(`/admin?section=consolidaties&cid=${doc.consolidation_id}`, "_blank");
    } else {
      window.open("/admin?section=documenten", "_blank");
    }
  };

  return (
    <div
      className="bg-white border border-gray-200"
      data-testid="recent-documents-widget"
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#0055FF]" />
          <h3 className="text-xs uppercase font-bold text-[#0A192F] tracking-wide">
            Recente Documenten
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={load}
            className="p-1 hover:bg-white rounded"
            title="Vernieuwen"
            data-testid="recent-documents-refresh"
          >
            <RefreshCw className={`w-3 h-3 text-gray-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          <a
            href="/admin?section=documenten"
            className="text-[10px] uppercase tracking-wide text-[#0055FF] font-bold hover:underline"
            data-testid="recent-documents-hub-link"
          >
            Alle
          </a>
        </div>
      </div>
      <div className="max-h-[380px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-4 h-4 animate-spin mx-auto text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-gray-400">
            Nog geen recente documenten.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
                data-testid={`recent-doc-${doc.id}`}
              >
                <div className="text-gray-500 flex-shrink-0">
                  {TYPE_ICONS[doc.type]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#0A192F] truncate">
                    {doc.number} · {doc.type_label}
                  </div>
                  <div className="text-[10px] text-gray-500 truncate">
                    {doc.customer_name} · {fmtTimeAgo(doc.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
                  title="Downloaden"
                  data-testid={`recent-doc-download-${doc.id}`}
                >
                  <Download className="w-3 h-3 text-[#0055FF]" />
                </button>
                {(doc.shipment_id || doc.consolidation_id) && (
                  <button
                    onClick={() => openSource(doc)}
                    className="p-1 hover:bg-gray-100 rounded flex-shrink-0"
                    title="Open zending/consolidatie"
                    data-testid={`recent-doc-open-${doc.id}`}
                  >
                    <ExternalLink className="w-3 h-3 text-gray-400" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
